/**
 * useSystemConfig Hook
 * React Query hooks for pricing system configuration
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import settingsService from '../services/settingsService';
import { toast } from 'react-hot-toast';

/**
 * Hook for fetching pricing presets and validation limits
 */
export function usePresets() {
  return useQuery({
    queryKey: ['pricingPresets'],
    queryFn: () => settingsService.getPresets(),
    staleTime: 1000 * 60 * 30, // 30 minutes - presets rarely change
  });
}

/**
 * Hook for fetching pricing settings (includes system_config)
 */
export function usePricingSettings() {
  return useQuery({
    queryKey: ['pricingSettings'],
    queryFn: () => settingsService.getPricingSettings(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook for updating pricing settings (including system_config)
 */
export function useUpdatePricingSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updates) => settingsService.updatePricingSettings(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricingSettings'] });
      toast.success('Pricing settings updated');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Failed to update settings');
    },
  });
}

/**
 * Hook for estimating LLM calls
 */
export function useEstimateCalls() {
  return useMutation({
    mutationFn: (systemConfig) => settingsService.estimateCalls(systemConfig),
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Failed to estimate calls');
    },
  });
}

/**
 * Helper to apply a schedule preset to system config
 */
export function applySchedulePreset(presets, presetName, currentConfig) {
  if (!presets?.schedule_presets?.[presetName]) {
    return currentConfig;
  }

  const preset = presets.schedule_presets[presetName];
  return {
    ...currentConfig,
    schedule_tiers: {
      preset: presetName,
      tier_1: { ...preset.tiers.tier_1 },
      tier_2: { ...preset.tiers.tier_2 },
      tier_3: { ...preset.tiers.tier_3 },
      tier_4: { ...preset.tiers.tier_4 },
    },
  };
}

/**
 * Helper to apply a lookback preset to system config
 */
export function applyLookbackPreset(presets, presetName, currentConfig) {
  if (!presets?.lookback_presets?.[presetName]) {
    return currentConfig;
  }

  const preset = presets.lookback_presets[presetName];
  return {
    ...currentConfig,
    lookback_config: {
      preset: presetName,
      ...preset.config,
    },
  };
}

/**
 * Default system config (matches backend defaults)
 */
export const DEFAULT_SYSTEM_CONFIG = {
  forecast_days: 30,
  schedule_enabled: true,
  schedule_tiers: {
    preset: 'default',
    tier_1: { days_start: 30, days_end: 7, interval_hours: 24 },
    tier_2: { days_start: 7, days_end: 4, interval_hours: 4 },
    tier_3: { days_start: 4, days_end: 1, interval_hours: 2 },
    tier_4: { days_start: 1, days_end: 0, interval_hours: 1 },
  },
  lookback_config: {
    preset: 'default',
    general_pricing_days: 30,
    booking_velocity_hours: 24,
    price_elasticity_days: 90,
    cancellation_rate_days: 90,
    conversion_rate_days: 30,
    discount_effectiveness_days: 90,
    search_lead_time_days: 90,
  },
};

export default {
  usePresets,
  usePricingSettings,
  useUpdatePricingSettings,
  useEstimateCalls,
  applySchedulePreset,
  applyLookbackPreset,
  DEFAULT_SYSTEM_CONFIG,
};
